import React, { FC, useState, useRef } from 'react';
import {
  Card,
  CardTitle,
  CardBody,
  Typography,
  IconButton,
  MenuItem,
  Menu
} from '@one-thd/sui-atomic-components';
import { More } from '@one-thd/sui-icons';
import { IGiftCardCommonData } from '../../models/common-models';
import { isInActive, updateUrlParam, formatMaskedCardNumber } from '../../utils/general';
import { setButtonEvent, setViewpurchasesEvent } from '../../utils/analytics';

interface IGiftCardProps {
  giftCard: IGiftCardCommonData;
  isB2B: boolean;
  children: React.ReactNode;
  isReloadDisabled: boolean;
  showScanToPayButton?: boolean;
  onDelete: () => void;
  onEdit: () => void;
  onReload: () => void;
  onShowHelp: () => void;
  onScanToPay?: (paymentId: string) => void;
}

export const GiftCard: FC<IGiftCardProps> = ({
  giftCard,
  children,
  isB2B,
  isReloadDisabled,
  showScanToPayButton = false,
  onDelete,
  onEdit,
  onReload,
  onShowHelp,
  onScanToPay
}): JSX.Element => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLInputElement>(null);
  const { nickName, actions, cardDetails } = giftCard;
  const cardLast4 = cardDetails?.cardNumberLast4;
  const isLegacyCard = actions?.isLegacyCard;
  const isInActiveCard = isInActive(isLegacyCard, actions?.onlineTransactable);
  const cardTextColor = isInActiveCard ? 'inactive' : 'primary';

  const handleViewPurchases = () => {
    setViewpurchasesEvent();
    updateUrlParam(
      [
        {
          key: 'requestSearchSelectorValue',
          newValue: 'giftCard'
        },
        {
          key: 'requestSearchCriteria',
          newValue: cardLast4
        },
        { key: 'page', newValue: null },
        { key: 'sortBy', newValue: null },
        { key: 'selectedTab', newValue: null }
      ],
      '/myaccount/purchase-history'
    );
  };

  const handleReload = () => {
    setButtonEvent('cards and accounts: gift cards: meatball: reload');
    onReload();
  };

  const handleScanToPay = () => {
    setButtonEvent('cards and accounts: gift cards: meatball: scan to pay');
    if (onScanToPay && giftCard.id) {
      onScanToPay(giftCard.id);
    } else {
      // eslint-disable-next-line no-console
      console.warn('⚠️ [GiftCard] Cannot proceed - missing callback or id:', {
        hasCallback: !!onScanToPay,
        hasPaymentId: !!giftCard.id
      });
    }
  };

  const getActions = () => {
    const _menuItems = [];

    const { reload, viewPurchases, edit, delete: isDelete } = actions;
    if (edit) {
      _menuItems.push({ value: 'Edit', onClick: onEdit });
    }
    if (viewPurchases && isB2B) {
      _menuItems.push({ value: 'View Purchases', onClick: handleViewPurchases });
    }
    if (reload && !isReloadDisabled) {
      _menuItems.push({
        value: isLegacyCard ? 'Check Balance and Reload' : 'Reload',
        onClick: handleReload
      });
    }
    // Add Scan to Pay menu item if feature is enabled and callback is provided
    if (showScanToPayButton && onScanToPay) {
      _menuItems.push({ value: 'Scan to Pay', onClick: handleScanToPay });
    }
    if (isDelete) {
      _menuItems.push({ value: 'Delete', onClick: onDelete });
    }
    _menuItems.push({ value: 'Need Help', onClick: onShowHelp });
    return _menuItems;
  };

  const menuItems = getActions();

  const handleOnClick = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (anchorRef.current && event.target && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const renderActions = () => {
    if (!menuItems || !menuItems.length) {
      return null;
    }
    return (
      <Menu
        id="history-menu"
        anchorEl={anchorRef.current}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': 'history-button', role: 'listbox' }}
        placement="bottom-end"
        className="sui-z-50"
      >
        {menuItems.map((item) => (
          <MenuItem
            key={item.value}
            onClick={() => {
              setOpen(false);
              if (item.onClick) {
                item.onClick();
              }
            }}
            data-testid={`menu-item-${item.value.toLowerCase().replace(/\s/g, '-')}`}
          >
            {item.value}
          </MenuItem>
        ))}
      </Menu>
    );
  };

  return (
    <Card grow>
      <CardTitle
        grow
        disableTypography
        disableActionSpacing
        header={
          nickName ? (
            <Typography
              variant="h5"
              weight="bold"
              data-testid="gift-card-nick-name"
              color={cardTextColor}
            >
              {nickName}
            </Typography>
          ) : (
            <div className="sui-opacity-0">
              <Typography variant="h5" weight="display" color={cardTextColor}>
                Gift Card
              </Typography>
            </div>
          )
        }
        subheader={
          cardLast4 && (
            <Typography variant="body-base" color={cardTextColor}>
              {formatMaskedCardNumber(cardLast4)}
            </Typography>
          )
        }
        action={
          menuItems
          && menuItems.length && (
            <IconButton
              icon={More}
              aria-expanded={open ? 'true' : undefined}
              id="history-button"
              onClick={handleOnClick}
              data-testid="icon-button"
              ref={anchorRef}
              aria-haspopup="listbox"
            />
          )
        }
      />
      {renderActions()}
      <CardBody>{children}</CardBody>
    </Card>
  );
};
